import {
  Box,
  Button,
  Flashbar,
  Modal,
  SpaceBetween,
  TextContent,
} from '@cloudscape-design/components';
import { ProcessNames, ProcessStepMap } from '@parallel-fluidics/shared/constants';
import ProjectSelector from 'components/HMI/components/projectSelector';
import {
  PRESS_HMI_BONDING_CONFIG_FIELDS,
  PRESS_HMI_FORMING_CONFIG_FIELDS,
} from 'features/plc/contracts/pressHMI.fields';
import { PLC_PATHS } from 'features/plc/contracts/pressHMI.schema';
import {
  getCompatiblePressHMIBondingConfigValues,
  getCompatiblePressHMIFormingConfigValues,
} from 'features/plc/contracts/pressHMIConfig';
import { usePLC, usePLCValue } from 'features/plc/providers/PLCProvider';
import fetchLatestProcessLogForPress from 'features/processLog/api/fetchLatestProcessLogForPress';
import { Formik } from 'formik';
import ConfigSection from 'pages/PressHMI/components/configSection';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

export default function LoadConfigModal({
  isOpen,
  close,
  process,
}) {
  const configFields = useMemo(
    () => (
      process === ProcessNames.FORMING
        ? PRESS_HMI_FORMING_CONFIG_FIELDS
        : PRESS_HMI_BONDING_CONFIG_FIELDS
    ),
    [process],
  );
  const getCompatibleConfigValues = useMemo(
    () => (process === ProcessNames.FORMING
      ? getCompatiblePressHMIFormingConfigValues
      : getCompatiblePressHMIBondingConfigValues),
    [process],
  );
  const { write } = usePLC();
  const pressId = usePLCValue(PLC_PATHS.meta.pressId);
  const schemaVersion = usePLCValue(PLC_PATHS.meta.schemaVersion);

  const initialValues = useMemo(() => ({
    project: null,
    notFound: false,
    config: configFields.reduce((accumulator, field) => ({
      ...accumulator,
      [field.key]: '',
    }), {}),
  }), [configFields]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values) => {
        await Promise.all(configFields
          .filter((field) => values.config[field.key] !== '' && values.config[field.key] !== undefined)
          .map((field) => write({
            handle: field.path.name,
            value: values.config[field.key],
          })));
        close();
      }}
    >
      {({
        values,
        handleSubmit,
        setFieldValue,
        errors,
      }) => (
        <Modal
          size="max"
          visible={isOpen}
          onDismiss={close}
          header={`Load ${process} configuration`}
          footer={(
            <Box float="right">
              <SpaceBetween direction="horizontal" size="m">
                <Button variant="link" onClick={close}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={values.notFound || values.project === 'placeholder-item'}
                >
                  Load
                </Button>
              </SpaceBetween>
            </Box>
          )}
        >
          <SpaceBetween size="m">
            <TextContent>
              Select a project below to load the most recent production run for this project.
              Check the values before clicking &quot;Load.&quot;
            </TextContent>
            <ProjectSelector
              stepType={ProcessStepMap[process]}
              enablePastProjectSearch
              value={values.project}
              setProject={async (newProject) => {
                configFields.forEach((field) => {
                  setFieldValue(`config.${field.key}`, '');
                });
                setFieldValue('project', newProject);

                if (newProject?.value === 'other') {
                  return;
                }

                try {
                  const processLog = await fetchLatestProcessLogForPress({
                    press: pressId,
                    project: newProject.projectId,
                    process,
                    production: true,
                    stepPPartId: newProject.stepPPart || null,
                  });

                  if (!processLog) {
                    setFieldValue('notFound', true);
                    return;
                  }

                  const configValues = getCompatibleConfigValues({
                    plcConfig: processLog.plcConfig,
                    currentSchemaVersion: schemaVersion,
                  });

                  if (!configValues || Object.keys(configValues).length === 0) {
                    setFieldValue('notFound', true);
                    return;
                  }

                  setFieldValue('notFound', false);
                  configFields.forEach((field) => {
                    if (configValues[field.key] !== undefined) {
                      setFieldValue(`config.${field.key}`, configValues[field.key]);
                    }
                  });
                } catch {
                  setFieldValue('notFound', true);
                }
              }}
              error={errors.project}
            />
            {values.notFound ? (
              <Flashbar
                items={[{
                  header: 'No compatible configuration found for this project.',
                  content: 'This means there have been no production runs for this project, or the saved configuration is not compatible with the current PLC schema version.',
                  type: 'error',
                  dismissible: false,
                  id: 'config_not_found',
                }]}
              />
            ) : null}
            <ConfigSection
              configFields={configFields}
              configValues={values.config}
            />
          </SpaceBetween>
        </Modal>
      )}
    </Formik>
  );
}

LoadConfigModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  process: PropTypes.oneOf(['forming', 'bonding']).isRequired,
};
